import { cloneDeep } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { DefaultFetchService } from "app/services/fetchService";
import { stateService } from "app/user-data/StateService";
import { updateWindowUser } from "single-spa/bootstrap-utils";
import type { IFavoriteObject, IFavorites, IStateObject } from "app/@types/userdata";

const ADD_ENDPOINT = "api/userdata/favorites/add";
const REMOVE_ENDPOINT = "api/userdata/favorites/remove/";

const DEFAULT_FAVORITES: IFavorites = Object.freeze({ items: [], freeSlots: 0, virtualItems: [] });

class FavoritesServiceClass {
    private static instance: FavoritesServiceClass;

    private favorites: IFavorites = DEFAULT_FAVORITES;
    private rawFavorites: IFavorites = DEFAULT_FAVORITES;
    private _isDirty = false;

    private constructor() {
        // Self-hydrate from the pre-fetched promise that starts in loadUserAssets.html.
        void window.similarwebPromises?.favoritesPromise
            ?.then((res: Response) => (res?.ok ? res.json() : null))
            .then((data: IFavorites | null) => {
                if (!this._isDirty) {
                    const favorites = data ?? DEFAULT_FAVORITES;
                    this.init(favorites);
                    updateWindowUser("favorites", favorites);
                }
            })
            .catch((err) => {
                Sentry.errorTracker({
                    message: "[FavoritesService] hydration failed, using defaults",
                    e: err,
                    level: "warning",
                });
            });
    }

    static getInstance(): FavoritesServiceClass {
        if (!FavoritesServiceClass.instance) {
            FavoritesServiceClass.instance = new FavoritesServiceClass();
        }
        return FavoritesServiceClass.instance;
    }

    private consolidate(): void {
        this.favorites = cloneDeep(this.rawFavorites);
        this.favorites.items = stateService.applyDefaults(this.favorites.items);
        if (this.favorites.virtualItems) {
            this.favorites.virtualItems = stateService.applyDefaults(this.favorites.virtualItems);
        }
    }

    private updateLocal(promise: Promise<IFavorites>): void {
        this._isDirty = true;
        void promise
            .then((newFavorites) => {
                this.rawFavorites = newFavorites;
                this.consolidate();
            })
            .catch(() => {
                // rejection is handled by the caller via the returned promise
            });
    }

    init(favoritesArg: IFavorites = DEFAULT_FAVORITES): void {
        this.rawFavorites = favoritesArg;
        this.favorites = DEFAULT_FAVORITES;
    }

    getFavorites(): IFavorites {
        if (this.favorites === DEFAULT_FAVORITES) {
            this.consolidate();
        }
        return this.favorites;
    }

    findFavorite(navObj: IStateObject): IFavoriteObject | undefined {
        return this.getFavorites().items?.find((fav) => {
            const favoritesSupportTypes = ["website", "app"];
            if (fav.data.type === navObj.type && favoritesSupportTypes.includes(fav.data.type)) {
                return fav.data.mainItem === navObj.mainItem;
            }
            return false;
        });
    }

    addFavorite(state: IStateObject, endpoint: string = ADD_ENDPOINT): Promise<IFavorites> {
        const fetchService = DefaultFetchService.getInstance();
        const promise = fetchService.post<IFavorites>(endpoint, state);
        this.updateLocal(promise);
        return promise;
    }

    removeFavoriteById({ id }: Partial<IFavoriteObject>, endpoint = REMOVE_ENDPOINT): Promise<IFavorites> {
        const fetchService = DefaultFetchService.getInstance();
        const promise = fetchService.post<IFavorites>(endpoint + id, { id });
        this.updateLocal(promise);
        return promise;
    }

    async addCurrentPageToFavorites(): Promise<IFavorites> {
        const state = await stateService.serializeState();
        if (state) {
            return this.addFavorite(state);
        }
        return DEFAULT_FAVORITES;
    }

    async removeCurrentPageFromFavorites(): Promise<IFavorites> {
        const state = await stateService.serializeState();
        const fav = state ? this.findFavorite(state) : undefined;
        if (!fav) {
            return this.getFavorites();
        }
        return this.removeFavoriteById(fav);
    }

    async isCurrentPageFavorite(): Promise<boolean> {
        const state = await stateService.serializeState();
        return !!state && Boolean(this.findFavorite(state));
    }
}

export const FavoritesService = FavoritesServiceClass.getInstance();
